import React, { useState, useEffect } from 'react';
import HeaderOrg from '../Headers/HeaderOrg';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { Container } from 'react-bootstrap';
import { serviceConsumer } from '../../network/ServiceConsumer';
import Loader from '../../Components/Loader/Loader';
import Help from '../../Components/Help/Help';
import Back from '../../Components/Back/Back';
import GoLiveConfirmationModal from '../../Components/Modals/OrganizerModel/GoLiveConfirmationModal';
import ConfirmOfflineModal from '../../Components/Modals/OrganizerModel/ConfirmOfflineModal';
import ToggleSwitch from '../../Components/ToggleSwitch/ToggleSwitch';
import { ShowToastMessage } from '../../helper/ShowToastMessage';
import { formatDate } from '../../helper/formatDate';
import { renderCollab } from './renderFns';
import { getCollabdetails } from './getCollabdetails';

// Final review + go-live for the Oireachtas wizard. Dedicated page (NOT the
// shared FinalFiezDetails) per the wizard convention: the feis/grade sections
// render cap options / feis_date / closing_date etc. that an Oireachtas
// doesn't have, and prop-gating all of them risks regressing feis. Same CSS
// classes so it looks identical. Go Live publishes immediately (admin-only
// wizard — no organizer-request/admin-approve split like feis), and a live
// event can be taken back offline from the same button (live is a toggle).
const FinalOireachtasDetails = () => {
  const [loading, setLoading] = useState(false);
  const [apiLoading, setApiLoading] = useState(false);
  const [eventDetails, setEventDetails] = useState();
  const [adjudicators, setAdjudicators] = useState([]);
  const [musicians, setMusicians] = useState([]);
  const [openGoLiveModal, setOpenGoLiveModal] = useState(false);
  const [openOfflineModal, setOpenOfflineModal] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const EventID = params.get('id') ? params.get('id') : localStorage.getItem('ID');
  const navigate = useNavigate();

  useEffect(() => {
    getEventDetails();
  }, []);

  const getEventDetails = async () => {
    try {
      if (!EventID) return;
      setLoading(true);
      const url = `${process.env.REACT_APP_BASE_URL}/oireachtas/${EventID}`;
      const response = await serviceConsumer('GET', url);
      const event = response.event;
      setEventDetails(event);
      if (!event) return;
      const promises = [];
      if (event.adjudicators?.length > 0) getCollabdetails(event.adjudicators, setAdjudicators, promises);
      if (event.musicians?.length > 0) getCollabdetails(event.musicians, setMusicians, promises);
      await Promise.all(promises);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Dedicated apiLoading (not the page-level `loading` that unmounts the whole
  // body) so a toggle overlays the Loader without losing the rendered page.
  const setLiveHandler = async (target) => {
    setOpenOfflineModal(false);
    setApiLoading(true);
    try {
      const url = `${process.env.REACT_APP_BASE_URL}/oireachtas/golive`;
      const response = await serviceConsumer('POST', url, { id: EventID, live: target });
      if (response.success === true) {
        if (target) {
          // Wizard complete — clear the draft keys before leaving.
          ['ID', 'TAB', 'page', 'organizerId', 'previousEvent'].forEach((k) => localStorage.removeItem(k));
          setOpenGoLiveModal(true);
        } else {
          ShowToastMessage('success', 'Event taken offline');
        }
        // Merge only the toggled flags — the page keeps its GET-hydrated
        // shape rather than swapping in the POST's raw saved doc.
        setEventDetails((prev) => ({ ...prev, live: target, orgLive: target }));
      }
      setApiLoading(false);
    } catch (error) {
      ShowToastMessage('error', target ? 'Could not publish the event' : 'Could not take the event offline');
      setApiLoading(false);
    }
  };

  // Switch handler: OFF goes through the confirm modal; ON publishes directly.
  const onLiveToggle = () => {
    if (eventDetails?.live) setOpenOfflineModal(true);
    else setLiveHandler(true);
  };

  // Edit icons re-open the wizard on the right tab. Keep the draft keys armed
  // so the wizard unlocks all completed tabs.
  const editHandler = (tab) => {
    localStorage.setItem('ID', EventID);
    localStorage.setItem('TAB', String(tab));
    if (!localStorage.getItem('page')) localStorage.setItem('page', eventDetails?.page || '6');
    navigate(`/oireachtas/create?id=${EventID}`);
  };

  const fmt = (d) => (d ? moment(formatDate(d)).format('MM/DD/YYYY') : '—');
  const money = (v) => (v || v === 0 ? `${v} ${eventDetails?.currency || ''}` : null);
  const fees = eventDetails?.fees || {};

  const detail = (heading, value) =>
    value === null || value === undefined || value === '' ? null : (
      <React.Fragment key={heading}>
        <div className="detailsHeading">{heading}</div>
        <span className="LiveData">{value}</span>
      </React.Fragment>
    );

  return (
    <>
      <HeaderOrg />
      {(loading || apiLoading) && <Loader />}
      {openGoLiveModal && <GoLiveConfirmationModal openModal={openGoLiveModal} setOpenModal={setOpenGoLiveModal} from="goLive" type="Oireachtas" />}
      {openOfflineModal && <ConfirmOfflineModal open={openOfflineModal} onCancel={() => setOpenOfflineModal(false)} onConfirm={() => setLiveHandler(false)} />}
      {!loading && (
        <div className="goLiveMainDIv">
          <Container style={{ background: '#FFFFFF', marginBottom: '10px' }}>
            <div className="headingOrg p-0">
              <div className="d-flex backContainer" style={{ color: '#68AD3F', cursor: 'pointer' }}>
                <Back onBackClick={() => navigate('/oireachtas')} />
                <div className="organizerExam"></div>
              </div>
            </div>
            <div className="EventDetailss ">
              <div className="finalMainHead">
                <div className="logoImg">
                  {eventDetails?.logo && <img src={eventDetails?.logo} alt="logo" />}
                  <span className="EventNAme">{eventDetails?.name}</span>
                </div>
              </div>
              <div className="d-flex justify-content-between">
                <div>
                  <span className="ExamNAme p-0">Oireachtas</span>
                  <span className="examVenue"> {fmt(eventDetails?.eventDays?.[0])}</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="manage-title-type">Live</span>
                  <ToggleSwitch checked={!!eventDetails?.live} onChange={onLiveToggle} />
                </div>
              </div>
            </div>
          </Container>
          <div className="goLiveMAin"></div>
          <section className="golive-section">
            <div className="container pb-5">
              <div className="row align-items-start ROwLive">
                <div className="col-lg-6 mainDivLive">
                  <span className="eventDetailsBlock">Event Details</span>
                  <BorderColorIcon className="editIcon" onClick={() => editHandler(1)} />
                  <div className="eventsHr" style={{ marginBottom: '17px', height: '1.1px' }}></div>
                  {detail('Name of Oireachtas:', eventDetails?.name)}
                  {detail('Contact Email:', eventDetails?.contact_email)}
                  {detail('Region:', eventDetails?.region)}
                  {detail('Currency:', eventDetails?.currency)}
                  {detail('Event Days:', (eventDetails?.eventDays || []).map(fmt).join(', '))}
                  {detail('Reg. Open Date:', fmt(eventDetails?.opening_date))}
                  {detail('Entries Close:', fmt(eventDetails?.entriesCloseDate))}
                  {eventDetails?.lateEntryCloseDate && detail('Late Entries Close:', fmt(eventDetails?.lateEntryCloseDate))}
                  {eventDetails?.changeFreezeDate && detail('Changes Freeze:', fmt(eventDetails?.changeFreezeDate))}
                </div>
                <div className="col-lg-6 mainDivLive" id="sndDiv">
                  <span className="eventDetailsBlock">Venue Details</span>
                  <BorderColorIcon className="editIcon" onClick={() => editHandler(2)} />
                  <div className="eventsHr"></div>
                  {detail('Venue Name:', eventDetails?.venue_name)}
                  {detail('Address:', eventDetails?.venue_address)}
                  <div>{eventDetails?.venue_city}</div>
                  <div>{eventDetails?.state}</div>
                  <div>{eventDetails?.country}</div>
                  <div>{eventDetails?.postal_code}</div>
                </div>
              </div>
              <div className="row align-items-start ROwLive">
                <div className="col-lg-6 mainDivLive">
                  <span className="eventDetailsBlock">About</span>
                  <BorderColorIcon className="editIcon" onClick={() => editHandler(5)} />
                  <div className="eventsHr" style={{ marginBottom: '17px' }}></div>
                  <span className="LiveData">{eventDetails?.description || '—'}</span>
                </div>
                <div className="col-lg-6 mainDivLive" id="sndDiv">
                  <span className="eventDetailsBlock">Fees</span>
                  <BorderColorIcon className="editIcon" onClick={() => editHandler(3)} />
                  <div className="eventsHr" style={{ marginBottom: '17px' }}></div>
                  {detail('Family Fee:', money(fees.familyFee))}
                  {detail('Family Fee Cap:', money(fees.familyFeeCap))}
                  {detail('School Assessment (per dancer):', money(fees.schoolAssessmentPerDancer))}
                  {fees.lateFee?.value != null &&
                    detail('Late Fee:', fees.lateFee.mode === 'percent' ? `${fees.lateFee.value}%` : money(fees.lateFee.value))}
                  {detail('Change Fee:', money(fees.changeFee))}
                  {detail('Solo Championship Price:', money(fees.soloChampPrice))}
                  {detail('Premiere Price:', money(fees.premierePrice))}
                  {detail('Treble Reel Price:', money(fees.trebleReelPrice))}
                  {detail('Trad Set Price:', money(fees.tradSetPrice))}
                  {detail('Team Price (per comp):', money(fees.teamPricePerComp))}
                </div>
              </div>
              {(adjudicators?.length > 0 || musicians?.length > 0) && (
                <div style={{ paddingBottom: '3rem' }}>
                  <div className="col-lg-6 mainDivLive ">
                    <span className="eventDetailsBlock">Complete Collaborators</span>
                    <BorderColorIcon className="editIcon" onClick={() => editHandler(6)} />
                    <div className="eventsHr" style={{ marginBottom: '17px' }}></div>
                    {adjudicators?.length > 0 && renderCollab('Adjudicators:', adjudicators)}
                    {musicians?.length > 0 && renderCollab('Musicians:', musicians)}
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      )}
      <Help />
    </>
  );
};

export default FinalOireachtasDetails;
